"use client";

import React, { useState, useLayoutEffect } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Logo from "../../assets/SalonTym_Logo.png";
import {
  Box,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
  styled,
  Stack,
  alpha,
  useTheme,
  useMediaQuery,
  keyframes,
} from "@mui/material";
import Image from "next/image";
import { ApiService } from "../../services/AppService";
import {
  ResponseObject,
  CustomerOtpVerificationRequest,
} from "../../classes/main";
import { saveToken } from "../../services/HolderService";
import SignUpProfile from "../profile/SignUpProfile";
import { trackEvent } from "@/app/utils/branch";
import Link from "next/link";
import { Loader } from "../genericComponents/MainLoader/Loader";
import { motion } from "framer-motion";

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const Input = styled(TextField)(({ theme }) => ({
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    display: "none",
  },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 14,
    backgroundColor: alpha(theme.palette.common.white, 0.55),
    transition: "box-shadow 0.22s ease, background-color 0.22s ease",
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    backgroundColor: alpha("#fff", 0.92),
    boxShadow: `
      0 0 0 1px ${alpha("#1f7a4d", 0.35)},
      0 0 24px ${alpha("#1f7a4d", 0.28)},
      0 0 48px ${alpha("#34d399", 0.12)}
    `,
  },
}));

interface LoginProps {
  showLoginBox: any;
  handleLoginClose: any;
  handleUserLoggedIn: any;
}

const Login: React.FC<LoginProps> = ({
  showLoginBox,
  handleLoginClose,
  handleUserLoggedIn,
}) => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("md"));
  const apiService = new ApiService();
  const [mobileNumber, SetMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [validNumber, setValidNumber] = useState(false);
  const [validOtp, setValidOtp] = useState(false);
  const [signUpForm, setSignUpForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("91");

  /** After OTP, profile (registration) replaces login — reset scroll so signup isn’t stuck below the fold. */
  useLayoutEffect(() => {
    if (!showLoginBox) return;
    const reset = () => {
      const root = document.querySelector(".MuiDialog-root");
      const paper = root?.querySelector(
        ".MuiDialog-paper"
      ) as HTMLElement | null;
      if (paper) paper.scrollTop = 0;
      const container = root?.querySelector(
        ".MuiDialog-container"
      ) as HTMLElement | null;
      if (container) container.scrollTop = 0;
    };
    reset();
    requestAnimationFrame(reset);
  }, [signUpForm, showLoginBox]);

  const handleCodeChange = (event: any) => {
    setCountryCode(event.target.value);
  };

  const handleMobileNumber = (e: any) => {
    SetMobileNumber(e.target.value);
  };

  const handleOtp = (e: any) => {
    setOtp(e.target.value);
  };

  const handleClose = () => {
    setSignUpForm(false);
    setShowOtpInput(false);
    setValidNumber(false);
    setValidOtp(false);
    SetMobileNumber("");
    setOtp("");
    handleLoginClose();
  };

  const handleLoginCloseAfterSignUp = () => {
    handleLoginClose();
    setSignUpForm(false);
  };

  async function sendOtpToCustomer() {
    const responseObject: ResponseObject | undefined =
      await apiService.get<ResponseObject>(
        apiService.sendOtp +
          "?username=" +
          mobileNumber +
          "&prefix=" +
          countryCode
      );

    if (responseObject?.status == "success") {
      setShowOtpInput(true);
      setValidNumber(false);
    } else {
    }
  }
  async function verifyOtp() {
    const customerVerification: CustomerOtpVerificationRequest =
      new CustomerOtpVerificationRequest();
    customerVerification.username = mobileNumber;
    customerVerification.otp = otp;
    customerVerification.prefix = countryCode;
    const responseObject: ResponseObject | undefined = await apiService.post(
      apiService.verifyOtp,
      customerVerification
    );
    if (responseObject?.status == "success") {
      if (responseObject?.object?.statusCode == 1) {
        setValidNumber(false);
        setShowOtpInput(false);
        handleUserLoggedIn();
        SetMobileNumber("");
        setOtp("");
        saveToken(responseObject.message);
        setSignUpForm(true);
        trackEvent("LOGIN", null);
      } else {
        setValidNumber(false);
        setShowOtpInput(false);
        handleUserLoggedIn();
        SetMobileNumber("");
        setOtp("");
        saveToken(responseObject.message);
        setSignUpForm(false);
        handleLoginClose();
        trackEvent("LOGIN", null);
        window.location.reload();
      }
    } else {
      alert(responseObject?.message);
    }
  }

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const mobileNumberVaidation =
      mobileNumber && mobileNumber.toString().length === 10;
    if (mobileNumberVaidation) {
      if (mobileNumberVaidation && showOtpInput === true) {
        if (otp) {
          verifyOtp();
        } else {
          setValidOtp(true);
        }
      } else {
        sendOtpToCustomer();
      }
    } else {
      setValidNumber(true);
    }
  };

  const paperGlowSx = {
    position: "relative" as const,
    overflow: "hidden",
    borderRadius: fullScreenDialog ? 0 : 3,
    maxWidth: fullScreenDialog ? "100%" : 440,
    width: "100%",
    background:
      "linear-gradient(165deg, rgba(255,255,255,0.97) 0%, rgba(246,252,248,0.98) 42%, rgba(255,253,245,0.96) 100%)",
    border: `1px solid ${alpha("#1f7a4d", 0.18)}`,
    boxShadow: `
      0 0 0 1px ${alpha("#fff", 0.55)} inset,
      0 0 32px ${alpha("#1f7a4d", 0.22)},
      0 0 72px ${alpha("#34d399", 0.12)},
      0 0 120px ${alpha("#86efac", 0.06)},
      0 28px 64px ${alpha("#0b1710", 0.14)}
    `,
  };

  return (
    <React.Fragment>
      {loading && <Loader />}
      <Dialog
        open={showLoginBox}
        onClose={handleClose}
        closeAfterTransition={false}
        fullScreen={fullScreenDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: paperGlowSx }}
        BackdropProps={{
          sx: {
            backgroundColor: alpha("#050a08", 0.55),
            backdropFilter: "blur(10px)",
            backgroundImage: `
              radial-gradient(ellipse 80% 60% at 50% 40%, ${alpha("#1f7a4d", 0.22)} 0%, transparent 55%),
              radial-gradient(ellipse 60% 50% at 80% 80%, ${alpha("#f2c46b", 0.12)} 0%, transparent 50%)
            `,
          },
        }}
      >
        {!signUpForm ? (
          <Box
            key="login-panel"
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: fullScreenDialog ? "100dvh" : "auto",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* Soft inner light orbs */}
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: "-20%",
                left: "-15%",
                width: "70%",
                height: "55%",
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha("#86efac", 0.35)} 0%, transparent 68%)`,
                filter: "blur(2px)",
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                bottom: "-10%",
                right: "-20%",
                width: "65%",
                height: "50%",
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha("#f2c46b", 0.22)} 0%, transparent 65%)`,
                pointerEvents: "none",
                zIndex: 0,
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                px: 1.5,
                pt: 1.5,
                position: "relative",
                zIndex: 2,
              }}
            >
              <IconButton
                onClick={handleClose}
                aria-label="Close login"
                size="small"
                sx={{
                  color: "text.secondary",
                  bgcolor: alpha("#0f1711", 0.05),
                  boxShadow: `0 0 20px ${alpha("#1f7a4d", 0.12)}`,
                  "&:hover": {
                    bgcolor: alpha("#0f1711", 0.09),
                    boxShadow: `0 0 28px ${alpha("#1f7a4d", 0.2)}`,
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <Stack
                spacing={2.5}
                sx={{
                  px: { xs: 2.5, sm: 3 },
                  pb: 3,
                  pt: 0,
                  alignItems: "center",
                  textAlign: "center",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <Box
                  sx={{
                    filter: `drop-shadow(0 0 20px ${alpha("#1f7a4d", 0.25)})`,
                  }}
                >
                  <Image
                    src={Logo}
                    alt="Salontym"
                    width={160}
                    height={48}
                    priority
                    style={{
                      width: "160px",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                <Typography
                  variant="overline"
                  sx={{
                    letterSpacing: "0.35em",
                    fontWeight: 700,
                    color: alpha("#1f7a4d", 0.85),
                    textShadow: `0 0 24px ${alpha("#34d399", 0.45)}`,
                  }}
                >
                  Welcome
                </Typography>

                <Box>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 800,
                      color: "text.primary",
                      letterSpacing: "-0.02em",
                      fontSize: { xs: "1.35rem", sm: "1.5rem" },
                      textShadow: `0 1px 0 ${alpha("#fff", 0.8)}`,
                    }}
                  >
                    {showOtpInput ? "Enter OTP" : "Log in or sign up"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.75,
                      maxWidth: 320,
                      mx: "auto",
                      lineHeight: 1.55,
                    }}
                  >
                    {showOtpInput
                      ? `Code sent to +${countryCode} ${mobileNumber}`
                      : "We'll text you a one-time code to verify your number."}
                  </Typography>
                </Box>

                <Stack spacing={1.5} sx={{ width: "100%", maxWidth: 360 }}>
                  <Input
                    autoFocus
                    margin="dense"
                    type="number"
                    label="Mobile number"
                    placeholder="10-digit number"
                    value={mobileNumber}
                    onChange={handleMobileNumber}
                    fullWidth
                    disabled={showOtpInput}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Select
                            value={countryCode}
                            onChange={handleCodeChange}
                            variant="standard"
                            disableUnderline
                            disabled={showOtpInput}
                            sx={{
                              fontSize: "0.9rem",
                              minWidth: 72,
                              fontWeight: 700,
                            }}
                          >
                            <MenuItem value="91">🇮🇳 +91</MenuItem>
                            <MenuItem value="977">🇳🇵 +977</MenuItem>
                          </Select>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#1f7a4d",
                          borderWidth: 2,
                        },
                    }}
                  />

                  {validNumber && (
                    <Typography
                      variant="caption"
                      role="alert"
                      sx={{
                        color: "error.main",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Please enter a valid 10-digit mobile number
                    </Typography>
                  )}

                  {showOtpInput && (
                    <Stack spacing={1}>
                      <Input
                        autoFocus
                        label="One-time password"
                        type="number"
                        margin="dense"
                        fullWidth
                        onChange={handleOtp}
                        value={otp}
                        placeholder="6-digit OTP"
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#1f7a4d",
                              borderWidth: 2,
                            },
                        }}
                      />
                      <Box sx={{ textAlign: "right" }}>
                        <Button
                          type="button"
                          onClick={sendOtpToCustomer}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            color: "#1f7a4d",
                            textShadow: `0 0 12px ${alpha("#34d399", 0.35)}`,
                          }}
                        >
                          Resend OTP
                        </Button>
                      </Box>
                    </Stack>
                  )}

                  {validOtp && (
                    <Typography
                      variant="caption"
                      role="alert"
                      sx={{
                        color: "error.main",
                        textAlign: "left",
                        fontWeight: 600,
                      }}
                    >
                      Please enter the OTP sent to your phone
                    </Typography>
                  )}
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    maxWidth: 360,
                    py: 1.5,
                    minHeight: 50,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: "1rem",
                    color: "#fff",
                    background: `linear-gradient(120deg, #0f2d1f 0%, #1f7a4d 40%, #2a9d62 55%, #1f7a4d 100%)`,
                    backgroundSize: "200% 100%",
                    animation: `${shimmer} 5s linear infinite`,
                    boxShadow: `
                      0 0 24px ${alpha("#1f7a4d", 0.45)},
                      0 0 48px ${alpha("#34d399", 0.2)},
                      0 12px 28px ${alpha("#0b1710", 0.2)}
                    `,
                    "&:hover": {
                      background: `linear-gradient(120deg, #0a2218 0%, #1a6b42 45%, #248f55 100%)`,
                      boxShadow: `
                        0 0 32px ${alpha("#1f7a4d", 0.55)},
                        0 0 64px ${alpha("#86efac", 0.25)},
                        0 14px 32px ${alpha("#0b1710", 0.22)}
                      `,
                    },
                  }}
                >
                  {showOtpInput ? "Verify & continue" : "Send OTP"}
                </Button>

                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    maxWidth: 340,
                    mx: "auto",
                    lineHeight: 1.65,
                    px: 1,
                  }}
                >
                  By continuing, you agree to our{" "}
                  <Link
                    href="/privacy-policy"
                    style={{
                      color: "#1f7a4d",
                      fontWeight: 700,
                      textDecoration: "underline",
                    }}
                    onClick={() => setLoading(true)}
                  >
                    Terms & conditions
                  </Link>{" "}
                  and{" "}
                  <Link
                    href="/privacy-policy"
                    style={{
                      color: "#1f7a4d",
                      fontWeight: 700,
                      textDecoration: "underline",
                    }}
                    onClick={() => setLoading(true)}
                  >
                    Privacy policy
                  </Link>
                  .
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: alpha("#0f1711", 0.35),
                    fontSize: "0.68rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  }}
                >
                  Made with care in Noida, India
                </Typography>
              </Stack>
            </motion.div>
          </Box>
        ) : (
          <Box
            key="signup-panel"
            sx={{
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: fullScreenDialog ? "100dvh" : "auto",
              maxHeight: fullScreenDialog ? "100dvh" : "none",
              overflow: "hidden",
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                flexShrink: 0,
                py: 0.5,
                px: 0.5,
                order: 0,
              }}
            >
              <IconButton
                onClick={handleClose}
                aria-label="Close"
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                px: { xs: 1.25, sm: 1.5 },
                pb: { xs: 1.25, sm: 1.5 },
                pt: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <SignUpProfile
                handleLoginCloseAfterSignUp={handleLoginCloseAfterSignUp}
              />
            </Box>
          </Box>
        )}
      </Dialog>
    </React.Fragment>
  );
};

export default Login;
